<template>
  <div class="music-sports-container">
    <!-- 音乐展示区域 -->
    <div class="music-section" data-aos="fade-up">
      <div class="section-header">
        <h2 class="section-title">🎵 音乐品味</h2>
        <p class="section-subtitle">分享我喜欢的音乐和专辑</p>
      </div>
      
      <div class="music-content">
        <!-- 音乐播放器样式 -->
        <div class="music-player" data-aos="zoom-in" data-aos-delay="200">
          <div class="player-header">
            <div class="now-playing">
              <span class="playing-indicator">♪</span>
              <span class="playing-text">正在播放</span>
            </div>
            <div class="player-controls">
              <button class="control-btn" @click="toggleShuffle" :class="{ active: isShuffled }">🔀</button>
              <button class="control-btn" @click="toggleRepeat" :class="{ active: isRepeated }">🔁</button>
            </div>
          </div>
          
          <div class="album-display">
            <div class="album-cover" :class="{ spinning: isPlaying }">
              <img :src="currentSong.cover" :alt="currentSong.album" />
              <div class="vinyl-center"></div>
            </div>
            <div class="song-info">
              <h3 class="song-title">{{ currentSong.title }}</h3>
              <p class="song-artist">{{ currentSong.artist }}</p>
              <p class="song-album">{{ currentSong.album }}</p>
            </div>
          </div>
          
          <div class="progress-section">
            <div class="time-display">
              <span class="current-time">{{ formatTime(currentTime) }}</span>
              <span class="total-time">{{ formatTime(currentSong.duration) }}</span>
            </div>
            <div class="progress-bar" @click="seekTo">
              <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
              <div class="progress-thumb" :style="{ left: progressPercent + '%' }"></div>
            </div>
          </div>
          
          <div class="player-controls-main">
            <button class="control-btn" @click="previousSong">⏮️</button>
            <button class="play-pause-btn" @click="togglePlay">
              {{ isPlaying ? '⏸️' : '▶️' }}
            </button>
            <button class="control-btn" @click="nextSong">⏭️</button>
          </div>
        </div>
        
        <!-- 专辑网格 -->
        <div class="albums-grid" data-aos="fade-up" data-aos-delay="400">
          <h3 class="grid-title">我的音乐收藏</h3>
          <div class="albums-container">
            <div 
              v-for="(album, index) in musicCollection" 
              :key="index"
              class="album-card"
              @click="playAlbum(album)"
              :class="{ active: currentSong.album === album.name }"
            >
              <div class="album-cover-small">
                <img :src="album.cover" :alt="album.name" />
                <div class="play-overlay">
                  <span class="play-icon">▶️</span>
                </div>
              </div>
              <div class="album-info">
                <h4 class="album-name">{{ album.name }}</h4>
                <p class="album-artist">{{ album.artist }}</p>
                <div class="album-genre">{{ album.genre }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 运动展示区域 -->
    <div class="sports-section" data-aos="fade-up" data-aos-delay="600">
      <div class="section-header">
        <h2 class="section-title">🏃‍♂️ 运动生活</h2>
        <p class="section-subtitle">记录我的运动数据和健身成就</p>
      </div>
      
      <div class="sports-content">
        <!-- 运动统计仪表盘 -->
        <div class="sports-dashboard">
          <div class="dashboard-card" data-aos="flip-left" data-aos-delay="200">
            <div class="card-icon">🏃‍♂️</div>
            <div class="card-content">
              <div class="stat-number">{{ runningStats.totalDistance }}</div>
              <div class="stat-label">总里程 (公里)</div>
              <div class="stat-trend">
                <span class="trend-icon">📈</span>
                <span class="trend-text">本月 +{{ runningStats.monthlyIncrease }}km</span>
              </div>
            </div>
          </div>
          
          <div class="dashboard-card" data-aos="flip-left" data-aos-delay="300">
            <div class="card-icon">⏱️</div>
            <div class="card-content">
              <div class="stat-number">{{ runningStats.averagePace }}</div>
              <div class="stat-label">平均配速 (分钟/公里)</div>
              <div class="stat-trend">
                <span class="trend-icon">⚡</span>
                <span class="trend-text">个人最佳: {{ runningStats.bestPace }}</span>
              </div>
            </div>
          </div>
          
          <div class="dashboard-card" data-aos="flip-left" data-aos-delay="400">
            <div class="card-icon">🔥</div>
            <div class="card-content">
              <div class="stat-number">{{ runningStats.totalCalories }}</div>
              <div class="stat-label">总消耗卡路里</div>
              <div class="stat-trend">
                <span class="trend-icon">💪</span>
                <span class="trend-text">今日: {{ runningStats.todayCalories }}cal</span>
              </div>
            </div>
          </div>
          
          <div class="dashboard-card" data-aos="flip-left" data-aos-delay="500">
            <div class="card-icon">🏆</div>
            <div class="card-content">
              <div class="stat-number">{{ runningStats.totalRuns }}</div>
              <div class="stat-label">跑步次数</div>
              <div class="stat-trend">
                <span class="trend-icon">📅</span>
                <span class="trend-text">连续: {{ runningStats.streak }}天</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 成就徽章 -->
        <div class="achievements-section" data-aos="fade-up" data-aos-delay="700">
          <h3 class="achievements-title">运动成就</h3>
          <div class="achievements-grid">
            <div 
              v-for="(achievement, index) in achievements" 
              :key="index"
              class="achievement-badge"
              :class="{ unlocked: achievement.unlocked }"
            >
              <div class="badge-icon">{{ achievement.icon }}</div>
              <div class="badge-info">
                <h4 class="badge-title">{{ achievement.title }}</h4>
                <p class="badge-desc">{{ achievement.description }}</p>
                <div class="badge-date" v-if="achievement.unlocked">
                  {{ achievement.unlockedDate }}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 运动轨迹 -->
        <div class="running-routes" data-aos="fade-up" data-aos-delay="800">
          <h3 class="routes-title">常跑路线</h3>
          <div class="routes-container">
            <div 
              v-for="(route, index) in runningRoutes" 
              :key="index"
              class="route-card"
            >
              <div class="route-map">
                <div class="map-placeholder">
                  <span class="map-icon">🗺️</span>
                  <div class="route-path"></div>
                </div>
              </div>
              <div class="route-info">
                <h4 class="route-name">{{ route.name }}</h4>
                <div class="route-stats">
                  <span class="route-distance">📏 {{ route.distance }}km</span>
                  <span class="route-elevation">⛰️ {{ route.elevation }}m</span>
                  <span class="route-difficulty" :class="route.difficulty.toLowerCase()">
                    {{ route.difficulty }}
                  </span>
                </div>
                <p class="route-desc">{{ route.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MusicSports',
  data() {
    return {
      // 音乐相关数据
      isPlaying: false,
      isShuffled: false,
      isRepeated: false,
      currentTime: 0,
      currentSongIndex: 0,
      
      musicCollection: [
        {
          name: '夜曲',
          artist: '周杰伦',
          genre: '流行',
          cover: '/placeholder.svg?height=200&width=200'
        },
        {
          name: '稻香',
          artist: '周杰伦',
          genre: '流行',
          cover: '/placeholder.svg?height=200&width=200'
        },
        {
          name: '演员',
          artist: '薛之谦',
          genre: '流行',
          cover: '/placeholder.svg?height=200&width=200'
        },
        {
          name: '告白气球',
          artist: '周杰伦',
          genre: '流行',
          cover: '/placeholder.svg?height=200&width=200'
        },
        {
          name: '体面',
          artist: '于文文',
          genre: '流行',
          cover: '/placeholder.svg?height=200&width=200'
        },
        {
          name: '成都',
          artist: '赵雷',
          genre: '民谣',
          cover: '/placeholder.svg?height=200&width=200'
        }
      ],
      
      playlist: [
        {
          title: '夜曲',
          artist: '周杰伦',
          album: '十一月的萧邦',
          duration: 233,
          cover: '/placeholder.svg?height=300&width=300'
        },
        {
          title: '稻香',
          artist: '周杰伦',
          album: '魔杰座',
          duration: 223,
          cover: '/placeholder.svg?height=300&width=300'
        },
        {
          title: '演员',
          artist: '薛之谦',
          album: '绅士',
          duration: 266,
          cover: '/placeholder.svg?height=300&width=300'
        }
      ],
      
      // 运动相关数据
      runningStats: {
        totalDistance: 1250,
        monthlyIncrease: 85,
        averagePace: '5:30',
        bestPace: '4:45',
        totalCalories: 45600,
        todayCalories: 320,
        totalRuns: 156,
        streak: 12
      },
      
      achievements: [
        {
          icon: '🏃‍♂️',
          title: '跑步新手',
          description: '完成第一次5公里跑步',
          unlocked: true,
          unlockedDate: '2023-01-15'
        },
        {
          icon: '🏆',
          title: '马拉松挑战者',
          description: '完成半程马拉松',
          unlocked: true,
          unlockedDate: '2023-06-20'
        },
        {
          icon: '🔥',
          title: '燃脂达人',
          description: '单月消耗10000卡路里',
          unlocked: true,
          unlockedDate: '2023-08-31'
        },
        {
          icon: '⚡',
          title: '速度之王',
          description: '配速突破5分钟/公里',
          unlocked: false,
          unlockedDate: null
        },
        {
          icon: '📅',
          title: '坚持不懈',
          description: '连续跑步30天',
          unlocked: false,
          unlockedDate: null
        },
        {
          icon: '🌟',
          title: '全马勇士',
          description: '完成全程马拉松',
          unlocked: false,
          unlockedDate: null
        }
      ],
      
      runningRoutes: [
        {
          name: '公园环湖路线',
          distance: 5.2,
          elevation: 45,
          difficulty: '简单',
          description: '风景优美的湖边跑道，适合晨跑和放松训练'
        },
        {
          name: '山地挑战路线',
          distance: 8.5,
          elevation: 180,
          difficulty: '困难',
          description: '具有挑战性的山地路线，提升耐力和腿部力量'
        },
        {
          name: '城市夜跑路线',
          distance: 6.8,
          elevation: 25,
          difficulty: '中等',
          description: '穿越市中心的夜跑路线，灯火辉煌的城市夜景'
        }
      ]
    }
  },
  
  computed: {
    currentSong() {
      return this.playlist[this.currentSongIndex] || this.playlist[0]
    },
    
    progressPercent() {
      return (this.currentTime / this.currentSong.duration) * 100
    }
  },
  
  mounted() {
    this.startMusicTimer()
  },
  
  beforeUnmount() {
    if (this.musicTimer) {
      clearInterval(this.musicTimer)
    }
  },
  
  methods: {
    // 音乐播放器方法
    togglePlay() {
      this.isPlaying = !this.isPlaying
      if (this.isPlaying) {
        this.startMusicTimer()
      } else {
        this.stopMusicTimer()
      }
    },
    
    nextSong() {
      this.currentSongIndex = (this.currentSongIndex + 1) % this.playlist.length
      this.currentTime = 0
    },
    
    previousSong() {
      this.currentSongIndex = this.currentSongIndex === 0 
        ? this.playlist.length - 1 
        : this.currentSongIndex - 1
      this.currentTime = 0
    },
    
    toggleShuffle() {
      this.isShuffled = !this.isShuffled
    },
    
    toggleRepeat() {
      this.isRepeated = !this.isRepeated
    },
    
    playAlbum(album) {
      // 模拟播放专辑
      const songIndex = this.playlist.findIndex(song => song.album.includes(album.name))
      if (songIndex !== -1) {
        this.currentSongIndex = songIndex
        this.currentTime = 0
        this.isPlaying = true
        this.startMusicTimer()
      }
    },
    
    seekTo(event) {
      if (!event.target || typeof event.target.getBoundingClientRect !== 'function') {
        return
      }
      
      try {
        const rect = event.target.getBoundingClientRect()
        const percent = (event.clientX - rect.left) / rect.width
        this.currentTime = percent * this.currentSong.duration
      } catch (error) {
        console.warn('Error in seekTo:', error)
      }
    },
    
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },
    
    startMusicTimer() {
      this.musicTimer = setInterval(() => {
        if (this.isPlaying) {
          this.currentTime += 1
          if (this.currentTime >= this.currentSong.duration) {
            if (this.isRepeated) {
              this.currentTime = 0
            } else {
              this.nextSong()
            }
          }
        }
      }, 1000)
    },
    
    stopMusicTimer() {
      if (this.musicTimer) {
        clearInterval(this.musicTimer)
      }
    }
  }
}
</script>

<style scoped>
.music-sports-container {
  display: flex;
  flex-direction: column;
  gap: 4rem;
  padding: 2rem 0;
}

/* 通用区域样式 */
.music-section,
.sports-section {
  background: var(--bg-secondary);
  border-radius: 20px;
  padding: 3rem 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
}

.music-section::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(240, 147, 251, 0.03) 0%, transparent 70%);
  animation: rotate 25s linear infinite;
}

.sports-section::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(67, 233, 123, 0.03) 0%, transparent 70%);
  animation: rotate 30s linear infinite;
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  z-index: 1;
}

.section-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.section-subtitle {
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin: 0;
}

/* 音乐播放器样式 */
.music-content {
  position: relative;
  z-index: 1;
}

.music-player {
  background: linear-gradient(145deg, #2c2c2c, #1a1a1a);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 3rem;
  color: white;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
}

.player-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.now-playing {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.playing-indicator {
  color: #f093fb;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.playing-text {
  font-size: 0.9rem;
  opacity: 0.8;
}

.player-controls {
  display: flex;
  gap: 1rem;
}

.control-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.control-btn:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.control-btn.active {
  color: #f093fb;
  background: rgba(240, 147, 251, 0.2);
}

.album-display {
  display: flex;
  gap: 2rem;
  align-items: center;
  margin-bottom: 2rem;
}

.album-cover {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
  transition: transform 0.3s ease;
}

.album-cover.spinning {
  animation: spin 10s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.album-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.vinyl-center {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  background: #333;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid #666;
}

.song-info {
  flex: 1;
}

.song-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: white;
}

.song-artist {
  font-size: 1.1rem;
  color: #f093fb;
  margin: 0 0 0.3rem 0;
}

.song-album {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

.progress-section {
  margin-bottom: 1.5rem;
}

.time-display {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
}

.progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  cursor: pointer;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #f093fb, #f5576c);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-thumb {
  position: absolute;
  top: -4px;
  width: 14px;
  height: 14px;
  background: white;
  border-radius: 50%;
  transform: translateX(-50%);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  transition: left 0.3s ease;
}

.player-controls-main {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
}

.play-pause-btn {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f093fb, #f5576c);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(240, 147, 251, 0.4);
}

.play-pause-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(240, 147, 251, 0.6);
}

/* 专辑网格样式 */
.albums-grid {
  position: relative;
  z-index: 1;
}

.grid-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2rem;
  text-align: center;
}

.albums-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.album-card {
  background: var(--bg-tertiary);
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.album-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border-color: #f093fb;
}

.album-card.active {
  border-color: #f093fb;
  background: linear-gradient(135deg, rgba(240, 147, 251, 0.1), rgba(245, 87, 108, 0.1));
}

.album-cover-small {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  margin: 0 auto 1rem;
  position: relative;
}

.album-cover-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.album-card:hover .play-overlay {
  opacity: 1;
}

.play-icon {
  color: white;
  font-size: 1.5rem;
}

.album-info {
  text-align: center;
}

.album-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.3rem 0;
}

.album-artist {
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0 0 0.5rem 0;
}

.album-genre {
  font-size: 0.8rem;
  color: #f093fb;
  background: rgba(240, 147, 251, 0.1);
  padding: 0.2rem 0.6rem;
  border-radius: 10px;
  display: inline-block;
}

/* 运动区域样式 */
.sports-content {
  position: relative;
  z-index: 1;
}

.sports-dashboard {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.dashboard-card {
  background: var(--bg-tertiary);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
}

.dashboard-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(67, 233, 123, 0.1), transparent);
  transition: left 0.5s ease;
}

.dashboard-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.dashboard-card:hover::before {
  left: 100%;
}

.card-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  display: block;
}

.card-content {
  position: relative;
  z-index: 1;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #43e97b;
  margin-bottom: 0.5rem;
  display: block;
}

.stat-label {
  font-size: 1rem;
  color: var(--text-secondary);
  margin-bottom: 1rem;
  display: block;
}

.stat-trend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.trend-icon {
  font-size: 1rem;
}

/* 成就徽章样式 */
.achievements-section {
  margin-bottom: 3rem;
}

.achievements-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2rem;
  text-align: center;
}

.achievements-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.achievement-badge {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--bg-tertiary);
  border-radius: 15px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  opacity: 0.5;
  border: 2px solid var(--border-color);
}

.achievement-badge.unlocked {
  opacity: 1;
  border-color: #43e97b;
  background: linear-gradient(135deg, rgba(67, 233, 123, 0.1), rgba(56, 249, 215, 0.1));
}

.achievement-badge:hover {
  transform: translateX(5px);
}

.badge-icon {
  font-size: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #43e97b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.achievement-badge:not(.unlocked) .badge-icon {
  background: #666;
  filter: grayscale(100%);
}

.badge-info {
  flex: 1;
}

.badge-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.3rem 0;
}

.badge-desc {
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
}

.badge-date {
  font-size: 0.8rem;
  color: #43e97b;
  font-weight: 500;
}

/* 跑步路线样式 */
.running-routes {
  margin-bottom: 2rem;
}

.routes-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2rem;
  text-align: center;
}

.routes-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
}

.route-card {
  background: var(--bg-tertiary);
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid var(--border-color);
}

.route-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.route-map {
  height: 150px;
  background: linear-gradient(135deg, #43e97b, #38f9d7);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-placeholder {
  text-align: center;
  color: white;
  position: relative;
}

.map-icon {
  font-size: 3rem;
  display: block;
  margin-bottom: 0.5rem;
}

.route-path {
  position: absolute;
  top: 20%;
  left: 10%;
  right: 10%;
  height: 3px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 2px;
}

.route-path::before {
  content: '';
  position: absolute;
  top: -2px;
  left: 0;
  width: 7px;
  height: 7px;
  background: white;
  border-radius: 50%;
}

.route-path::after {
  content: '';
  position: absolute;
  top: -2px;
  right: 0;
  width: 7px;
  height: 7px;
  background: white;
  border-radius: 50%;
}

.route-info {
  padding: 1.5rem;
}

.route-name {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
}

.route-stats {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.route-distance,
.route-elevation {
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.route-difficulty {
  padding: 0.2rem 0.6rem;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 500;
}

.route-difficulty.简单 {
  background: rgba(76, 175, 80, 0.2);
  color: #4caf50;
}

.route-difficulty.中等 {
  background: rgba(255, 152, 0, 0.2);
  color: #ff9800;
}

.route-difficulty.困难 {
  background: rgba(244, 67, 54, 0.2);
  color: #f44336;
}

.route-desc {
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .music-sports-container {
    gap: 3rem;
    padding: 1rem 0;
  }
  
  .music-section,
  .sports-section {
    padding: 2rem 1rem;
  }
  
  .album-display {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .albums-container {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
  }
  
  .sports-dashboard {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
  
  .achievements-grid {
    grid-template-columns: 1fr;
  }
  
  .routes-container {
    grid-template-columns: 1fr;
  }
  
  .route-stats {
    justify-content: center;
  }
}

@media (max-width: 576px) {
  .section-title {
    font-size: 1.8rem;
  }
  
  .music-player {
    padding: 1.5rem;
  }
  
  .album-cover {
    width: 100px;
    height: 100px;
  }
  
  .song-title {
    font-size: 1.2rem;
  }
  
  .player-controls-main {
    gap: 1rem;
  }
  
  .play-pause-btn {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
  
  .albums-container {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .sports-dashboard {
    grid-template-columns: 1fr;
  }
  
  .achievement-badge {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
}

/* 夜间模式适配 */
.dark-mode .music-section,
.dark-mode .sports-section {
  background: var(--bg-secondary);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
}

.dark-mode .album-card,
.dark-mode .dashboard-card,
.dark-mode .achievement-badge,
.dark-mode .route-card {
  background: var(--bg-tertiary);
}

.dark-mode .music-player {
  background: linear-gradient(145deg, #1a1a1a, #2c2c2c);
}
</style>
